import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { FileText, Mic, MicOff, Upload, Volume2, VolumeX, X, Send, ChevronDown } from 'lucide-react';
import { formatMarkdownToHtml } from '../../utils/markdown';
import { useLanguage } from '../../contexts/LanguageContext';
import welbyImg from '../../assets/images/welby.PNG';

// ─── Constants ───────────────────────────────────────────────────────────────

const TIFFANY_MAX_SPEECH_CHARACTERS = 1200;
const MAX_FILE_COUNT = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TEXT_FILE_CHARACTERS = 12000;

const FILE_ACCEPT = [
    '.pdf', '.png', '.jpg', '.jpeg', '.webp',
    '.txt', '.csv', '.json', '.xml', '.md',
    'application/pdf', 'image/png', 'image/jpeg', 'image/webp',
    'text/plain', 'text/csv', 'application/json',
    'application/xml', 'text/xml', 'text/markdown'
].join(',');

const TEXT_MIME_TYPES = new Set([
    'text/plain', 'text/csv', 'application/csv',
    'application/json', 'application/xml', 'text/xml', 'text/markdown'
]);

const BINARY_MIME_TYPES = new Set([
    'application/pdf', 'image/png', 'image/jpeg', 'image/webp'
]);

const MIME_BY_EXTENSION = {
    pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg',
    jpeg: 'image/jpeg', webp: 'image/webp', txt: 'text/plain',
    csv: 'text/csv', json: 'application/json', xml: 'application/xml', md: 'text/markdown'
};

const LANGUAGE_NAMES = { en: 'English', es: 'Spanish', tl: 'Tagalog' };
const SPEECH_LOCALES = { en: 'en-US', es: 'es-US', tl: 'tl-PH' };

const WELCOME_MESSAGES = {
    en: "Hi, I'm Welby — your UME Health companion. Ask me about healthcare, health insurance, appointments, prescriptions, recovery steps, or follow-up planning. I provide friendly informational support, not medical advice.",
    es: 'Hola, soy Welby, tu compañero de UME Health. Puedes preguntarme sobre salud, seguro médico, citas, medicamentos, recuperación o planificación de seguimiento.',
    tl: 'Hi, ako si Welby — ang iyong UME Health companion. Maaari mo akong tanungin tungkol sa healthcare, health insurance, appointments, prescriptions, recovery steps, o follow-up planning.'
};

// ─── Utilities ────────────────────────────────────────────────────────────────

const getFileExtension = (file) =>
    file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';

const getSupportedMimeType = (file) => {
    const ext = getFileExtension(file);
    const mime = file.type || MIME_BY_EXTENSION[ext] || '';
    if (TEXT_MIME_TYPES.has(mime) || BINARY_MIME_TYPES.has(mime)) return mime;
    return MIME_BY_EXTENSION[ext] || '';
};

const isTextFile = (file) => TEXT_MIME_TYPES.has(getSupportedMimeType(file));

const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const units = ['B', 'KB', 'MB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / Math.pow(1024, i);
    return `${size.toFixed(size >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const readAsText = (file) =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ''));
        r.onerror = () => reject(r.error);
        r.readAsText(file);
    });

const readAsBase64 = (file) =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || '').split(',')[1] || '');
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
    });

const buildFileParts = async (files) => {
    const parts = [];
    for (const file of files) {
        const mimeType = getSupportedMimeType(file);
        parts.push({ text: `Attached file: ${file.name}\nType: ${mimeType}\nSize: ${formatFileSize(file.size)}` });
        if (isTextFile(file)) {
            const contents = await readAsText(file);
            const truncated = contents.length > MAX_TEXT_FILE_CHARACTERS;
            parts.push({
                text: [
                    `Readable contents from "${file.name}"${truncated ? ' (truncated)' : ''}:`,
                    contents.slice(0, MAX_TEXT_FILE_CHARACTERS) || '[Empty file]'
                ].join('\n')
            });
        } else {
            const data = await readAsBase64(file);
            parts.push({ inlineData: { data, mimeType } });
        }
    }
    return parts;
};

const buildFileReviewPrompt = (userText, files) => [
    `User request: ${userText}`,
    `Attached files: ${files.map((f) => f.name).join(', ')}`,
    'Review the uploaded content within Welby demo rules and healthcare topic limits.',
    'Keep the response very short and easy to scan.',
    'Put contact information and important dates first.',
    'Summarize in simple action steps using plain verbs: Call, Schedule, Ask, Add, Confirm, Track.',
    'Extract names, phone numbers, emails, clinic/pharmacy details, appointment dates, deadlines, referral details, and follow-up windows.',
    'If medication details appear, help organize as reminders or questions — never instruct to change medication.',
    'Add a "Simple summary:" line at the bottom with the most important point and next step.',
    'Do not diagnose, give treatment instructions, interpret results clinically, or advise changing care plans.'
].join('\n');

const cleanTextForSpeech = (text) =>
    String(text || '')
        .replace(/\*\*/g, '').replace(/[_`#>~]/g, '')
        .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
        .replace(/https?:\/\/\S+/g, 'a link')
        .replace(/\bUME\b/g, 'you me').replace(/\bAI\b/g, 'A I').replace(/\bPDFs?\b/g, 'P D F')
        .replace(/\s*[-•]\s+/g, '. ').replace(/\s+/g, ' ').trim();

// ─── Component ────────────────────────────────────────────────────────────────

const WelbyFloatingChat = () => {
    const { language } = useLanguage();
    const activeLangName = LANGUAGE_NAMES[language] || 'English';

    const systemPrompt = `You are Welby, a friendly AI health assistant for the UME Health landing page demo.

Your role:
- Answer questions related to healthcare, health insurance, medical terms, prescriptions, appointments, recovery, follow-up care, and other health-related topics.
- Speak in a warm, friendly, person-to-person way that feels supportive, calm, and natural.
- Keep responses concise and easy to understand. Use simple language and avoid jargon.
- Always respond in ${activeLangName} unless the user clearly asks you to switch languages.

Demo behavior:
- This is a landing page demo. If a user asks to schedule, reschedule, cancel, or move an appointment, pretend to do it and respond as if Welby completed the task successfully.
- Simulate the full assistant experience in a believable way.
- Treat scheduled meetings mentioned earlier in the same conversation as remembered context.

Uploaded document behavior:
- Users may upload medical documents, lab reports, visit summaries, insurance letters, images, or PDFs.
- Review uploads only within the healthcare/insurance/care-navigation scope.
- Give a short plain-language summary, then turn the document into simple action steps.
- Extract contact info and scheduling details when present.
- When medication details appear, help organize as reminders or questions. Never tell the user to start, stop, or change a medication.
- Do not claim the document was stored. Treat uploads as temporary demo context.
- Do not diagnose, provide treatment instructions, or interpret labs with clinical certainty.

Safety and scope:
- You cannot give clinical advice, emergency guidance, or medical diagnoses.
- Keep responses informational and educational.
- When appropriate, remind users to consult their doctor or care team.
- If the user mentions urgent symptoms, encourage them to seek immediate professional or emergency care.
- Occasionally (not every response) mention that joining the UME Health waitlist unlocks the full Welby experience.`;

    // Chat state
    const [isOpen, setIsOpen] = useState(false);
    const [panelPos, setPanelPos] = useState(null); // null = default CSS bottom-right
    const dragState = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0 });
    const sceneRef = useRef(null);
    const [messages, setMessages] = useState([{ type: 'welby', text: WELCOME_MESSAGES[language] || WELCOME_MESSAGES.en }]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);

    // Voice state
    const [speechInputSupported, setSpeechInputSupported] = useState(false);
    const [speechOutputSupported, setSpeechOutputSupported] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [botVoiceEnabled, setBotVoiceEnabled] = useState(false);
    const [isWelbySpeaking, setIsWelbySpeaking] = useState(false);

    // Refs
    const chatRef = useRef(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const speechRecognitionRef = useRef(null);
    const speechBaseQueryRef = useRef('');
    const tiffanyAudioRef = useRef(null);
    const tiffanyAudioUrlRef = useRef(null);
    const tiffanySpeechRequestRef = useRef(0);

    // Gemini setup
    const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
    const geminiModelName = process.env.REACT_APP_GEMINI_MODEL || 'gemini-2.5-flash-lite';
    const model = genAI.getGenerativeModel({ model: geminiModelName, systemInstruction: systemPrompt });

    // ── Effects ────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (typeof window === 'undefined') return;
        setSpeechInputSupported(Boolean(window.SpeechRecognition || window.webkitSpeechRecognition));
        setSpeechOutputSupported(Boolean(window.fetch && window.URL && window.Blob && window.Audio));
        return () => {
            speechRecognitionRef.current?.stop();
            stopWelbySpeech(false);
        };
    }, []);

    useEffect(() => {
        // Reset chat when language changes
        chatRef.current = null;
        setMessages([{ type: 'welby', text: WELCOME_MESSAGES[language] || WELCOME_MESSAGES.en }]);
        setQuery('');
        setError(null);
        setSelectedFiles([]);
        setIsListening(false);
        speechRecognitionRef.current?.stop();
        stopWelbySpeech();
    }, [language]);

    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => textareaRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // ── Drag to reposition ─────────────────────────────────────────────────────

    const onDragStart = (e) => {
        if (e.button !== 0) return;
        const rect = sceneRef.current?.getBoundingClientRect();
        if (!rect) return;
        dragState.current = {
            dragging: true,
            startX: e.clientX,
            startY: e.clientY,
            originX: rect.left,
            originY: rect.top,
        };
        e.preventDefault();
    };

    useEffect(() => {
        const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

        const onMove = (e) => {
            if (!dragState.current.dragging) return;
            const dx = e.clientX - dragState.current.startX;
            const dy = e.clientY - dragState.current.startY;
            const sceneW = sceneRef.current?.offsetWidth ?? 420;
            const sceneH = sceneRef.current?.offsetHeight ?? 500;
            const maxX = window.innerWidth - sceneW - 8;
            const maxY = window.innerHeight - sceneH - 8;
            setPanelPos({
                left: clamp(dragState.current.originX + dx, 8, maxX),
                top:  clamp(dragState.current.originY + dy, 8, maxY),
            });
        };

        const onUp = () => { dragState.current.dragging = false; };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, []);

    // ── Voice: output ──────────────────────────────────────────────────────────

    const stopWelbySpeech = (updateState = true) => {
        tiffanySpeechRequestRef.current += 1;
        if (tiffanyAudioRef.current) {
            tiffanyAudioRef.current.pause();
            tiffanyAudioRef.current.currentTime = 0;
            tiffanyAudioRef.current = null;
        }
        if (tiffanyAudioUrlRef.current && typeof window !== 'undefined') {
            window.URL.revokeObjectURL(tiffanyAudioUrlRef.current);
            tiffanyAudioUrlRef.current = null;
        }
        if (updateState) setIsWelbySpeaking(false);
    };

    const speakWelbyText = async (text) => {
        if (!speechOutputSupported || !text || language !== 'en') return;
        stopWelbySpeech();
        const requestId = tiffanySpeechRequestRef.current;
        const prepared = cleanTextForSpeech(text).slice(0, TIFFANY_MAX_SPEECH_CHARACTERS);
        if (!prepared) return;

        setIsWelbySpeaking(true);
        setError(null);

        try {
            const response = await fetch('/api/voice/polly-tiffany', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: prepared, language })
            });
            if (!response.ok) throw new Error('Voice unavailable');

            const audioBlob = await response.blob();
            if (requestId !== tiffanySpeechRequestRef.current) return;

            const audioUrl = window.URL.createObjectURL(audioBlob);
            tiffanyAudioUrlRef.current = audioUrl;
            const audio = new window.Audio(audioUrl);
            tiffanyAudioRef.current = audio;

            audio.onended = () => { if (requestId === tiffanySpeechRequestRef.current) setIsWelbySpeaking(false); };
            audio.onerror = () => { if (requestId === tiffanySpeechRequestRef.current) setIsWelbySpeaking(false); };
            await audio.play();
        } catch {
            if (requestId === tiffanySpeechRequestRef.current) setIsWelbySpeaking(false);
        }
    };

    const handleBotVoiceToggle = () => {
        if (!speechOutputSupported || language !== 'en') {
            setError(language !== 'en' ? 'AWS Tiffany voice is available for English replies only.' : 'Voice output is not available right now.');
            setBotVoiceEnabled(false);
            stopWelbySpeech();
            return;
        }
        const next = !botVoiceEnabled;
        setBotVoiceEnabled(next);
        setError(null);
        if (!next) { stopWelbySpeech(); return; }
        const latest = [...messages].reverse().find((m) => m.type === 'welby');
        if (latest?.text) speakWelbyText(latest.text);
    };

    // ── Voice: input ───────────────────────────────────────────────────────────

    const handleVoiceToggle = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) { setError('Voice input is not supported in this browser.'); return; }

        if (isListening) { speechRecognitionRef.current?.stop(); setIsListening(false); return; }

        try {
            const recognition = new SpeechRecognition();
            speechBaseQueryRef.current = query.trim();
            recognition.lang = SPEECH_LOCALES[language] || 'en-US';
            recognition.continuous = false;
            recognition.interimResults = true;

            recognition.onstart = () => { setIsListening(true); setError(null); };
            recognition.onresult = (e) => {
                let final = '', interim = '';
                for (let i = 0; i < e.results.length; i++) {
                    const t = e.results[i][0].transcript;
                    e.results[i].isFinal ? (final += t) : (interim += t);
                }
                const spoken = [final, interim].join(' ').trim();
                if (spoken) setQuery([speechBaseQueryRef.current, spoken].filter(Boolean).join(' ').trim());
            };
            recognition.onerror = (e) => {
                setError(e.error === 'not-allowed' ? 'Microphone access was blocked.' : 'Could not hear clearly. Please try again.');
                setIsListening(false);
            };
            recognition.onend = () => setIsListening(false);

            speechRecognitionRef.current = recognition;
            recognition.start();
        } catch {
            setError('Voice input error. Please try again.');
            setIsListening(false);
        }
    };

    // ── File handling ──────────────────────────────────────────────────────────

    const handleFileChange = (e) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;

        const slots = MAX_FILE_COUNT - selectedFiles.length;
        const existing = new Set(selectedFiles.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
        const accepted = [];
        let nextError = null;

        if (slots <= 0) { setError('You can attach up to 3 files at a time.'); e.target.value = ''; return; }

        incoming.slice(0, slots).forEach((file) => {
            const key = `${file.name}-${file.size}-${file.lastModified}`;
            if (!getSupportedMimeType(file)) { nextError = 'Please attach a PDF, image, text, CSV, JSON, XML, or Markdown file.'; return; }
            if (file.size > MAX_FILE_SIZE) { nextError = 'Each file must be 10 MB or smaller.'; return; }
            if (!existing.has(key)) { accepted.push(file); existing.add(key); }
        });

        if (accepted.length) setSelectedFiles((prev) => [...prev, ...accepted]);
        setError(nextError);
        e.target.value = '';
    };

    const removeFile = (index) => setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

    // ── Submit ─────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = query.trim();
        const files = selectedFiles;
        if (!trimmed && files.length === 0) return;

        const displayText = trimmed || 'Please review the attached medical documents.';
        const attachments = files.map((f) => ({ name: f.name, size: f.size, type: getSupportedMimeType(f) }));

        setLoading(true);
        setError(null);

        try {
            const outgoing = files.length
                ? [{ text: buildFileReviewPrompt(displayText, files) }, ...(await buildFileParts(files))]
                : displayText;

            setMessages((prev) => [...prev, { type: 'user', text: displayText, attachments }]);
            setQuery('');
            setSelectedFiles([]);

            if (!chatRef.current) {
                chatRef.current = model.startChat({ history: [] });
            }

            const result = await chatRef.current.sendMessage(outgoing);
            const text = result.response.text();

            setMessages((prev) => [...prev, { type: 'welby', text }]);
            if (botVoiceEnabled) speakWelbyText(text);
        } catch (err) {
            console.error('Welby chat error:', err);
            setError(
                files.length
                    ? 'Welby could not read one of those files. Please try a different file.'
                    : 'Welby is temporarily unavailable. Please try again later.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <>
            {/* ── Floating Button ── */}
            {!isOpen && (
                <button
                    className="welby-fab"
                    onClick={() => setIsOpen(true)}
                    aria-label="Open Welby chat"
                >
                    <div className="welby-fab-avatar-wrapper">
                        <img src={welbyImg} alt="Welby" />
                    </div>
                    <span className="welby-fab-label">Chat with Welby</span>
                </button>
            )}

            {/* ── Scene: character + chat panel side by side ── */}
            {isOpen && (
                <div
                    className="welby-chat-scene"
                    ref={sceneRef}
                    style={panelPos ? {
                        left: panelPos.left,
                        top: panelPos.top,
                        bottom: 'auto',
                        right: 'auto',
                    } : {}}
                >

                    {/* Welby standing to the left */}
                    <div className="welby-character" aria-hidden="true">
                        <img src={welbyImg} alt="" />
                    </div>

                    {/* Chat panel */}
                    <div className="welby-chat-panel" role="dialog" aria-label="Welby chat">

                        {/* Header — drag handle */}
                        <div
                            className="welby-chat-header"
                            onMouseDown={onDragStart}
                            style={{ cursor: 'grab' }}
                        >
                            <div className="welby-chat-header-info">
                                <div>
                                    <div className="welby-chat-name">Welby</div>
                                    <div className={`welby-chat-status ${loading ? 'thinking' : ''}`}>
                                        {loading ? 'Thinking…' : 'Here with you'}
                                    </div>
                                </div>
                            </div>
                            <div className="welby-chat-header-actions">
                                <button
                                    className={`welby-icon-btn ${botVoiceEnabled ? 'active' : ''}`}
                                    onClick={handleBotVoiceToggle}
                                    title={botVoiceEnabled ? 'Voice on' : 'Voice off'}
                                    aria-pressed={botVoiceEnabled}
                                >
                                    {botVoiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                                </button>
                                <button
                                    className="welby-icon-btn"
                                    onClick={() => setIsOpen(false)}
                                    aria-label="Close chat"
                                >
                                    <ChevronDown size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="welby-chat-messages">
                            {messages.map((msg, i) => (
                                <div key={i} className={`welby-msg welby-msg--${msg.type}`}>
                                    <div className="welby-msg-bubble">
                                        <div
                                            className="welby-msg-text"
                                            dangerouslySetInnerHTML={{ __html: formatMarkdownToHtml(msg.text) }}
                                        />
                                        {msg.attachments?.length > 0 && (
                                            <div className="welby-msg-attachments">
                                                {msg.attachments.map((a, ai) => (
                                                    <span key={ai} className="welby-msg-attachment">
                            <FileText size={12} />
                                                        {a.name}
                          </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="welby-msg welby-msg--welby">
                                    <div className="welby-msg-bubble welby-msg-bubble--typing">
                                        <span /><span /><span />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Error */}
                        {error && <div className="welby-chat-error">{error}</div>}

                        {/* Selected files */}
                        {selectedFiles.length > 0 && (
                            <div className="welby-file-list">
                                {selectedFiles.map((file, i) => (
                                    <span key={`${file.name}-${i}`} className="welby-file-chip">
                    <FileText size={13} />
                    <span className="welby-file-chip-name">{file.name}</span>
                    <span className="welby-file-chip-size">{formatFileSize(file.size)}</span>
                    <button
                        type="button"
                        className="welby-file-chip-remove"
                        onClick={() => removeFile(i)}
                        aria-label={`Remove ${file.name}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                                ))}
                            </div>
                        )}

                        {/* Input area */}
                        <form className="welby-chat-input-area" onSubmit={handleSubmit}>
                            <div className="welby-chat-toolbar">
                                <button
                                    type="button"
                                    className={`welby-icon-btn ${isListening ? 'active' : ''}`}
                                    onClick={handleVoiceToggle}
                                    disabled={loading && !isListening}
                                    title={isListening ? 'Stop listening' : 'Voice input'}
                                    aria-pressed={isListening}
                                >
                                    {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                                </button>
                                <label
                                    className={`welby-icon-btn ${loading ? 'disabled' : ''}`}
                                    title="Attach files"
                                >
                                    <Upload size={16} />
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={FILE_ACCEPT}
                                        multiple
                                        onChange={handleFileChange}
                                        disabled={loading}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>

                            <div className="welby-chat-input-row">
                <textarea
                    ref={textareaRef}
                    className="welby-chat-textarea"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask Welby anything…"
                    disabled={loading}
                    rows={2}
                />
                                <button
                                    type="submit"
                                    className="welby-send-btn"
                                    disabled={loading || (!query.trim() && selectedFiles.length === 0)}
                                    aria-label="Send message"
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                            <p className="welby-chat-hint">Press Enter to send · Shift+Enter for new line</p>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default WelbyFloatingChat;